import React, { useState, useEffect, useRef } from 'react'; // Added useRef
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cal, { getCalApi } from "@calcom/embed-react";
// Import common components
// import { SaveConfirmationModal, DeleteConfirmationModal } from '../common/ConfirmationModals'; // Example path
import { useNotification } from '../common'; // Corrected import path
// Import CsvGuideModal
import CsvGuideModal from '../leads/CsvGuideModal';
// Import the new import components with column mapping
import ImportLeadsWithMapping from './ImportLeadsWithMapping';
import ImportDealsWithMapping from './ImportDealsWithMapping';

// Placeholder Step Components (Welcome, InviteTeam remain similar)
const WelcomeStep = ({ onNext }) => (
  <div>
    <h2 className="text-2xl font-semibold mb-4">Welcome to Your New CRM!</h2>
    <p className="text-gray-600 mb-6">We're excited to have you. Let's walk through a few steps to get your workspace ready.</p>
    <button
      onClick={onNext}
      className="w-full px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
    >
      Get Started
    </button>
  </div>
);

const InviteTeamStep = ({ onNext, onSkip, onPrev }) => {
  const { showNotification } = useNotification();
  const [inviteFormData, setInviteFormData] = useState({
    name: '',
    email: '',
    role: 'user', // Default role
  });
  const [availableRoles, setAvailableRoles] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [fetchingRoles, setFetchingRoles] = useState(true);

  // Fetch available roles when component mounts
  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setFetchingRoles(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication error. Please log in again.');
        setFetchingRoles(false);
        return;
      }

      const config = { headers: { 'x-auth-token': token } };
      const res = await axios.get('/api/roles', config);
      
      // Filter out system roles except for commonly needed ones
      const filteredRoles = res.data.filter(role => 
        !role.is_system_role || ['user', 'sales', 'admin'].includes(role.name)
      );
      
      setAvailableRoles(filteredRoles);
      setFetchingRoles(false);
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError('Failed to load available roles.');
      setFetchingRoles(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInviteFormData({ ...inviteFormData, [name]: value });
  };

  const addInvite = () => {
    // Basic validation
    if (!inviteFormData.name || !inviteFormData.email) {
      setError('Name and email are required for the invitation.');
      return;
    }

    // Email validation with regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteFormData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Check for duplicate email
    if (invites.some(invite => invite.email === inviteFormData.email)) {
      setError('This email has already been added to the invite list.');
      return;
    }

    // Add to invites list
    setInvites([...invites, { ...inviteFormData, id: Date.now() }]);
    
    // Clear form and error
    setInviteFormData({ name: '', email: '', role: inviteFormData.role });
    setError('');
  };

  const removeInvite = (id) => {
    setInvites(invites.filter(invite => invite.id !== id));
  };

  const sendInvites = async () => {
    if (invites.length === 0) {
      setError('Add at least one person to invite.');
      return;
    }

    setIsSending(true);
    setError('');
    let successCount = 0;
    let failCount = 0;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication error. Please log in again.');
        setIsSending(false);
        return;
      }

      // Process each invite sequentially
      for (const invite of invites) {
        try {
          const userData = {
            name: invite.name,
            email: invite.email,
            role: invite.role,
            sendInvite: true
          };

          const config = { 
            headers: { 
              'Content-Type': 'application/json', 
              'x-auth-token': token 
            } 
          };

          // Use the same endpoint as the NewEmployeeModal
          await axios.post('/api/auth/register', userData, config);
          successCount++;
        } catch (err) {
          console.error('Error sending invite:', err);
          failCount++;
        }
      }

      if (successCount > 0) {
        showNotification(`Successfully sent ${successCount} invitation${successCount > 1 ? 's' : ''}.`, 'success');
        
        // Clear invites that were successfully sent
        if (failCount === 0) {
          setInvites([]);
        }
        
        // If all invites were sent successfully, move to next step
        if (failCount === 0) {
          onNext();
        }
      }

      if (failCount > 0) {
        setError(`Failed to send ${failCount} invitation${failCount > 1 ? 's' : ''}.`);
      }
    } catch (err) {
      console.error('Error in send invites process:', err);
      setError('Failed to send invitations. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-4">Invite Your Team</h2>
      <p className="text-gray-600 mb-6">Invite colleagues to join you on the platform. They'll receive an email with instructions to set up their account.</p>
      
      {error && (
        <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Invite form */}
      <div className="mb-6 bg-gray-50 p-4 rounded-md border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Name*
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={inviteFormData.name}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Jane Doe"
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email*
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={inviteFormData.email}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="jane@example.com"
            />
          </div>
          
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
              Role*
            </label>
            <select
              id="role"
              name="role"
              value={inviteFormData.role}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={fetchingRoles}
            >
              {fetchingRoles ? (
                <option value="">Loading roles...</option>
              ) : (
                availableRoles.map(role => (
                  <option key={role.id} value={role.name}>
                    {role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            onClick={addInvite}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={loading || fetchingRoles}
          >
            Add to Invite List
          </button>
        </div>
      </div>

      {/* Invite list */}
      {invites.length > 0 && (
        <div className="mb-6">
          <h3 className="text-md font-medium mb-2">People to invite ({invites.length})</h3>
          <div className="overflow-hidden border border-gray-200 rounded-md">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invites.map(invite => (
                  <tr key={invite.id}>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{invite.name}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{invite.email}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                      {invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={() => removeInvite(invite.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="p-4 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded mb-6">
        <strong>Tip:</strong> You can always invite more team members later from the 'Employees' section in the main menu.
        <br /><br />
        <strong>Note:</strong> Invitations will be sent from adne@leadbooster.co - make sure your teammates check their spam folder if they don't receive the invitation.
      </div>
      
      <div className="flex justify-between mt-8">
        <button
          onClick={onPrev}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
          disabled={isSending}
        >
          Back
        </button>
        <div>
          <button
            onClick={onSkip}
            className="px-4 py-2 mr-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            disabled={isSending}
          >
            Skip for Now
          </button>
          {invites.length > 0 ? (
            <button
              onClick={sendInvites}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              disabled={isSending}
            >
              {isSending ? 'Sending Invites...' : 'Send Invites & Continue'}
            </button>
          ) : (
            <button
              onClick={onNext}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              disabled={isSending}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Import Leads Step has been replaced with ImportLeadsWithMapping component
// that uses the new CSV import flow with column mapping

// *** NEW: Company Setup Step ***
const CompanySetupStep = ({ onNext, onSkip, onPrev }) => { // Added onPrev
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    company_address: '',
    company_about: ''
    // Add other fields from CompanySettings if needed later, e.g., company_name
  });
  const [primaryLogo, setPrimaryLogo] = useState(null);
  const [primaryLogoPreview, setPrimaryLogoPreview] = useState('');
  const [alternativeLogo, setAlternativeLogo] = useState(null);
  const [alternativeLogoPreview, setAlternativeLogoPreview] = useState('');
  const [logoUsage, setLogoUsage] = useState({
    sidebar: 'primary',
    invoices: 'primary',
    email: 'primary'
  });
  const primaryLogoInputRef = useRef(null);
  const alternativeLogoInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch existing minimal settings if needed (optional for onboarding)
  // useEffect(() => { ... fetch logic ... }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePrimaryLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPrimaryLogo(file);
      setPrimaryLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleAlternativeLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAlternativeLogo(file);
      setAlternativeLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleLogoUsageChange = (location, value) => {
    setLogoUsage(prev => ({ ...prev, [location]: value }));
  };

  const handleSaveAndContinue = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication error.');
        setLoading(false);
        return;
      }

      const config = { headers: { 'Content-Type': 'application/json', 'x-auth-token': token } };

      // 1. Save text data (address, about)
      await axios.post('/api/settings/company/text', formData, config);

      // 2. Save logo usage
      await axios.post('/api/settings/company/logo-usage', { logoUsage }, config);

      // 3. Upload Primary Logo (if selected)
      if (primaryLogo) {
        const reader = new FileReader();
        const fileReadPromise = new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Failed to read primary logo file'));
        });
        reader.readAsDataURL(primaryLogo);
        const base64Data = await fileReadPromise;
        await axios.post('/api/settings/company/logo-base64', {
          filename: primaryLogo.name, mimeType: primaryLogo.type, size: primaryLogo.size, data: base64Data
        }, config);
      }

      // 4. Upload Alternative Logo (if selected)
      if (alternativeLogo) {
        const reader = new FileReader();
        const fileReadPromise = new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Failed to read alternative logo file'));
        });
        reader.readAsDataURL(alternativeLogo);
        const base64Data = await fileReadPromise;
        await axios.post('/api/settings/company/logo-alt-base64', {
          filename: alternativeLogo.name, mimeType: alternativeLogo.type, size: alternativeLogo.size, data: base64Data
        }, config);
      }

      showNotification('Company details saved!', 'success');
      onNext(); // Proceed to the next step in the wizard

    } catch (err) {
      console.error('Error saving company setup:', err);
      const message = err.response?.data?.message || 'Failed to save company details.';
      setError(message);
      showNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-4">Company Setup</h2>
      <p className="text-gray-600 mb-6">Tell us a bit about your company and add your branding.</p>

      {error && <p className="text-sm text-center text-red-600 bg-red-100 p-3 rounded mb-4">{error}</p>}

      <div className="space-y-4">
        {/* Company Address */}
        <div>
          <label htmlFor="company_address" className="block text-sm font-medium text-gray-700 mb-1">
            Company Address
          </label>
          <textarea
            id="company_address"
            name="company_address"
            value={formData.company_address}
            onChange={handleChange}
            rows="2"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="123 Main St, Anytown, USA 12345"
          />
        </div>

        {/* About the company */}
        <div>
          <label htmlFor="company_about" className="block text-sm font-medium text-gray-700 mb-1">
            About Your Company
          </label>
          <textarea
            id="company_about"
            name="company_about"
            value={formData.company_about}
            onChange={handleChange}
            rows="4"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Describe your company, services, values, and target audience. This helps AI generate better content later."
          />
           <p className="mt-1 text-xs text-gray-500">
            Used for AI features like email generation.
          </p>
        </div>

        {/* Logos Section */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h4 className="font-medium text-gray-800 mb-3">Company Logos</h4>

          {/* Grid container for logos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Primary Logo Upload Column */}
            <div> {/* Column 1 */}
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Logo</label>
              <div className="flex items-start space-x-4">
              <div className="mt-1 w-32 h-16 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50 overflow-hidden">
                {primaryLogoPreview ? <img src={primaryLogoPreview} alt="Primary Preview" className="max-w-full max-h-full object-contain" /> : <span className="text-xs text-gray-400">Preview</span>}
              </div>
              <div>
                <input type="file" ref={primaryLogoInputRef} onChange={handlePrimaryLogoChange} accept="image/*" className="sr-only" id="primary-logo-upload-wizard" />
                <label htmlFor="primary-logo-upload-wizard" className="cursor-pointer inline-block px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50">Select File</label>
                {primaryLogo && <span className="ml-2 text-sm text-gray-500">{primaryLogo.name}</span>}
                 <p className="mt-1 text-xs text-gray-500">Used by default (e.g., sidebar).</p>
              </div>
              </div>
            </div> {/* End Column 1 */}

            {/* Alternative Logo Upload Column */}
            <div> {/* Column 2 */}
              <label className="block text-sm font-medium text-gray-700 mb-1">Alternative Logo</label>
              <div className="flex items-start space-x-4">
              <div className="mt-1 w-32 h-16 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50 overflow-hidden">
                {alternativeLogoPreview ? <img src={alternativeLogoPreview} alt="Alt Preview" className="max-w-full max-h-full object-contain" /> : <span className="text-xs text-gray-400">Preview</span>}
              </div>
              <div>
                <input type="file" ref={alternativeLogoInputRef} onChange={handleAlternativeLogoChange} accept="image/*" className="sr-only" id="alt-logo-upload-wizard" />
                <label htmlFor="alt-logo-upload-wizard" className="cursor-pointer inline-block px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50">Select File</label>
                 {alternativeLogo && <span className="ml-2 text-sm text-gray-500">{alternativeLogo.name}</span>}
                 <p className="mt-1 text-xs text-gray-500">Optional, for specific uses.</p>
              </div>
              </div>
            </div> {/* End Column 2 */}

          </div> {/* End grid container for logos */}


          {/* Logo Usage Configuration */}
          <div className="mt-6"> {/* Added margin-top */}
            <h4 className="font-medium text-gray-800 mb-2 text-sm">Logo Usage</h4>
             <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
               <div>
                 <label className="block text-xs font-medium text-gray-600 mb-1">Sidebar</label>
                 <select value={logoUsage.sidebar} onChange={(e) => handleLogoUsageChange('sidebar', e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2">
                   <option value="primary">Primary</option>
                   <option value="alternative">Alternative</option>
                 </select>
               </div>
                <div>
                 <label className="block text-xs font-medium text-gray-600 mb-1">Invoices</label>
                 <select value={logoUsage.invoices} onChange={(e) => handleLogoUsageChange('invoices', e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2">
                   <option value="primary">Primary</option>
                   <option value="alternative">Alternative</option>
                 </select>
               </div>
                <div>
                 <label className="block text-xs font-medium text-gray-600 mb-1">Emails</label>
                 <select value={logoUsage.email} onChange={(e) => handleLogoUsageChange('email', e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2">
                   <option value="primary">Primary</option>
                   <option value="alternative">Alternative</option>
                 </select>
               </div>
             </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
         <button // Back button
          onClick={onPrev}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
          disabled={loading}
        >
          Back
        </button>
        <div> {/* Group Skip and Save */}
          <button
            onClick={onSkip} // Or just onNext if skipping isn't desired here
            className="px-4 py-2 mr-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            disabled={loading}
          >
            Skip for Now
          </button>
          <button
            onClick={handleSaveAndContinue}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};


// Setup Call Booking Step
const SetupCallStep = ({ onComplete, onPrev, onSkip }) => {
  useEffect(() => {
    (async function() {
      const cal = await getCalApi({"namespace":"strategy"});
      cal("ui", {"theme":"light","hideEventTypeDetails":false,"layout":"week_view"});
    })();
  }, []);

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-4">Book Your FREE Setup Call</h2>
      <p className="text-gray-600 mb-6">
        Let us help you get the most out of your new CRM! Schedule a free call and our team will help you:
      </p>
      <ul className="list-disc pl-6 mb-6 text-gray-600">
        <li>Set up lead syncing with your existing tools</li>
        <li>Import your contact data</li>
        <li>Configure automations for your workflow</li>
        <li>Customize your CRM to fit your business needs</li>
      </ul>
      
      <div className="border border-gray-200 rounded-lg shadow-sm h-[400px] mb-6">
        <Cal namespace="strategy"
          calLink="leadbooster/strategy"
          style={{width:"100%", height:"100%", overflow:"scroll"}}
          config={{"layout":"week_view","theme":"light"}}
        />
      </div>
      
      <div className="flex justify-between mt-8">
        <button
          onClick={onPrev}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
        >
          Back
        </button>
        <div>
          <button
            onClick={onSkip}
            className="px-4 py-2 mr-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
          >
            Skip for Now
          </button>
          <button
            onClick={onComplete}
            className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

// Final Step - Displayed after booking or skipping the setup call
const FinalStep = ({ onComplete, onPrev }) => ( // Added onPrev
   <div>
    <h2 className="text-2xl font-semibold mb-4">All Set!</h2>
    <p className="text-gray-600 mb-6">Your basic setup is complete. You can always adjust settings and invite more team members later from the main application menu.</p>
     <div className="p-4 mb-6 text-sm text-green-700 bg-green-100 border border-green-300 rounded">
       <strong>Ready to dive in?</strong> Click below to go to your dashboard.
    </div>
    <div className="flex justify-between mt-8"> {/* Use flex for alignment */}
       <button // Back button
        onClick={onPrev}
        className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
      >
        Back
      </button>
      <button
        onClick={onComplete}
        className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" // Adjusted padding/width
      >
        Go to Dashboard
      </button>
    </div>
  </div>
);


// Country and Currency Selection Step Component
const CountryCurrencyStep = ({ onNext, onPrev }) => {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    country: '',
    currency: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Country options - keeping only countries with native currencies that are supported
  const countries = [
    { code: 'NO', name: 'Norway' },      // NOK
    { code: 'SE', name: 'Sweden' },      // SEK
    { code: 'FI', name: 'Finland' },     // EUR
    { code: 'DE', name: 'Germany' },     // EUR
    { code: 'US', name: 'United States' }, // USD
    { code: 'CH', name: 'Switzerland' },  // CHF
    { code: 'AU', name: 'Australia' }    // AUD
  ];
  
  // Currency options with symbols - matches the ones synced by the currency service and CurrencySettings screen
  const currencies = [
    { code: 'NOK', name: 'Norwegian Krone (NOK)' },
    { code: 'SEK', name: 'Swedish Krona (SEK)' },
    { code: 'EUR', name: 'Euro (EUR)' },
    { code: 'USD', name: 'US Dollar (USD)' },
    { code: 'CHF', name: 'Swiss Franc (CHF)' },
    { code: 'AUD', name: 'Australian Dollar (AUD)' }
  ];

  // Suggested currency based on country selection
  const getDefaultCurrency = (countryCode) => {
    const countryToCurrency = {
      'NO': 'NOK',
      'SE': 'SEK',
      'FI': 'EUR',
      'DE': 'EUR',
      'US': 'USD',
      'CH': 'CHF',
      'AU': 'AUD'
    };
    return countryToCurrency[countryCode] || 'USD';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'country') {
      // Auto-select matching currency when country changes
      const defaultCurrency = getDefaultCurrency(value);
      setFormData({
        ...formData,
        country: value,
        currency: defaultCurrency
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async () => {
    // Validate input
    if (!formData.country || !formData.currency) {
      setError('Please select both country and currency');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication error. Please log in again.');
        setLoading(false);
        return;
      }

      // Save country to user profile
      await axios.post('/api/auth/update-profile',
        { country: formData.country },
        { headers: { 'x-auth-token': token } }
      );

      // Save currency as company default currency
      await axios.post('/api/settings/currencies',
        { settings: { company_currency: formData.currency } },
        { headers: { 'x-auth-token': token } }
      );

      // Get the selected country's name
      const selectedCountry = countries.find(c => c.code === formData.country);
      const countryName = selectedCountry ? selectedCountry.name : '';

      // Save company countries setting
      if (countryName) {
        await axios.post('/api/settings/company/countries',
          { countries: [countryName] },
          { headers: { 'x-auth-token': token } }
        );
      }

      // Enable prospector module if Norway is selected
      if (formData.country === 'NO') {
        await axios.put('/api/settings/modules',
          { prospector_enabled: true },
          { headers: { 'x-auth-token': token } }
        );
      }

      // Update local storage with country
      try {
        const userString = localStorage.getItem('user');
        if (userString) {
          const user = JSON.parse(userString);
          user.country = formData.country;
          localStorage.setItem('user', JSON.stringify(user));
        }
      } catch (storageError) {
        console.error('Error updating user in localStorage:', storageError);
      }

      showNotification('Location and currency settings saved!', 'success');
      onNext();
    } catch (err) {
      console.error('Error saving country/currency:', err);
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-4">Company Location & Currency</h2>
      <p className="text-gray-600 mb-6">
        Set your company's primary location and the currency you'll use for business transactions.
        This will also configure your relevant countries for forms and filters throughout the system.
      </p>

      {error && (
        <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
            Company Location
          </label>
          <select
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Select your country</option>
            {countries.map(country => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Used for default currency, tax calculations, localization, and country options in forms and filters.
          </p>
        </div>

        <div>
          <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
            Default Currency
          </label>
          <select
            id="currency"
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Select your default currency</option>
            {currencies.map(currency => (
              <option key={currency.code} value={currency.code}>
                {currency.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            This will be the default currency for all transactions and reports.
          </p>
        </div>

        <div className="p-4 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded">
          <p>
            <strong>Note:</strong> While you can change these settings later, we recommend setting them correctly now as they'll affect how monetary values are displayed throughout the system.
          </p>
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onPrev}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
          disabled={loading}
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save & Continue'}
        </button>
      </div>
    </div>
  );
};

const OnboardingWizard = () => {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  // Note: Loading/Error state is now managed within CompanySetupStep for API calls there.
  // We might need a top-level loading state for the final completion step if needed.
  const [isCompleting, setIsCompleting] = useState(false);
  const [completionError, setCompletionError] = useState('');
  const totalSteps = 8; // Define total steps for progress bar (Welcome, Country/Currency, Company Setup, Invite, Import Leads, Import Deals, Setup Call, Final)

  // Function to mark onboarding as complete by calling the backend API (Final Step)
  const markOnboardingComplete = async () => {
    setIsCompleting(true); // Use dedicated state for this final action
    setCompletionError('');
    try { // Added try block start
      const token = localStorage.getItem('token');
      if (!token) {
        setCompletionError('Authentication error. Please log in again.');
        navigate('/login');
        setIsCompleting(false);
        return;
      }

      // Call the NEW backend endpoint to complete COMPANY onboarding
      const response = await axios.post(
        '/api/settings/company/complete-onboarding', // Use the new endpoint
        {},
        // Use x-auth-token header as used elsewhere in the app
        { headers: { 'x-auth-token': token } }
      );

      console.log('Company onboarding marked as complete via API.');

      // Update user object in localStorage with the data returned from the API
      // The new endpoint returns the updated user object which includes company_onboarding_complete: true
      if (response.data && response.data.user) {
         try {
            localStorage.setItem('user', JSON.stringify(response.data.user));
            console.log('Updated user object in localStorage with data from API response.');
         } catch (storageError) {
            console.error('Error updating user object in localStorage:', storageError);
            // Proceed with navigation even if localStorage update fails,
            // as the backend state is the source of truth on next load.
         }
      } else {
         console.warn('API did not return updated user data after completing company onboarding.');
         // Attempt to manually update the flag as a fallback, though less ideal
         const userString = localStorage.getItem('user');
         if (userString) {
            try {
               const user = JSON.parse(userString);
               user.company_onboarding_complete = true; // Set the company flag
               localStorage.setItem('user', JSON.stringify(user));
               console.log('Manually updated company_onboarding_complete flag in localStorage user object.');
            } catch (storageError) {
               console.error('Error manually updating user object in localStorage:', storageError);
            }
         }
      }

      navigate('/'); // Redirect to dashboard immediately

    } catch (err) {
      console.error('Error marking onboarding complete via API:', err);
      setCompletionError(err.response?.data?.message || 'Could not finalize setup. Please try again.');
    } finally {
      setIsCompleting(false);
    }
  }; // End of markOnboardingComplete;

  // Function to go to the previous step
  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  }; // End of prevStep;

  const nextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  }; // End of nextStep;

  const skipStep = () => {
     if (step < totalSteps) {
      setStep(step + 1);
    }
  }; // End of skipStep;

  const renderStep = () => {
    switch (step) {
      case 1:
        // First step has no 'Back'
        return <WelcomeStep onNext={nextStep} />;
      case 2: // Country/Currency Selection - New first step after welcome
        return <CountryCurrencyStep onNext={nextStep} onPrev={prevStep} />;
      case 3: // Company Setup Step
        return <CompanySetupStep onNext={nextStep} onSkip={skipStep} onPrev={prevStep} />;
      case 4: // Invite Team
        return <InviteTeamStep onNext={nextStep} onSkip={skipStep} onPrev={prevStep} />;
      case 5: // Import Leads - Using ImportLeadsWithMapping with column mapping
        return <ImportLeadsWithMapping onNext={nextStep} onSkip={skipStep} onPrev={prevStep} />;
      case 6: // Import Deals - Using ImportDealsWithMapping with column mapping
        return <ImportDealsWithMapping onNext={nextStep} onSkip={skipStep} onPrev={prevStep} />;
      case 7: // Setup Call Step
        return <SetupCallStep onComplete={markOnboardingComplete} onSkip={() => {
          setStep(8); // Move to final step when skipping setup call
        }} onPrev={prevStep} />;
      case 8: // Final Step
        return <FinalStep onComplete={markOnboardingComplete} onPrev={prevStep} />;
      default:
        // Fallback
        navigate('/');
        return null;
    }
  }; // End of renderStep;


  return ( // Main return statement for the OnboardingWizard component
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100">
      <div className="w-full max-w-xl p-8 space-y-8 bg-white rounded-lg shadow-xl">
        <h1 className="text-xl font-bold text-center text-gray-700">Getting Started</h1>

        {/* Progress Bar - Updated to reflect totalSteps */}
        <div className="relative pt-1">
          <div className="flex mb-2 items-center justify-between">
            <div>
              <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-indigo-600 bg-indigo-200">
                Step {step} of {totalSteps}
              </span>
            </div>
          </div>
          <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-indigo-200">
            <div
              style={{ width: `${(step / totalSteps) * 100}%` }}
              className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-500 transition-all duration-500 ease-out"
            ></div>
          </div>
        </div>

        {/* Display completion error if any */}
        {completionError && <p className="text-sm text-center text-red-600 bg-red-100 p-3 rounded">{completionError}</p>}
        <div className="mt-6 min-h-[200px] flex flex-col justify-center"> {/* Added min-height */}
          {/* Show top-level loading indicator only during final completion step */}
          {isCompleting ? (
             <div className="text-center">
                <p className="text-lg font-semibold text-indigo-600">Finalizing setup...</p>
                {/* Optional: Add a spinner here */}
             </div>
          ) : (
            renderStep()
          )}
        </div>
      </div>
    </div>
  );
}; // End of OnboardingWizard component

export default OnboardingWizard;
